import AppConfig from '@/AppConfig';
import { IFloatingVariantProps } from '@/components/FloatingWidget/FloatingWidget';
import { SupportCenter } from '@/components/FloatingWidget/Variants';
import ClaimsBot from '@/components/FloatingWidget/Variants/CashlessBot';
import CultBot from '@/components/FloatingWidget/Variants/CultBot';
import { ALLOWED_CHILD_ORIGINS } from '@/constants';
import useModalRoute from '@/hooks/useModalRoute';
import { EMessageType, IMessagePayload } from '@/types';
import { postMessage } from '@/utils';
import {
  isTrustedRelaySource,
  runRelayedNativeBridgeCall,
} from '@/utils/nativeBridgeRelay';
import safeJsonParse from '@/utils/safeJsonParse';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

export interface IFloatingWidgetContext {
  isFloatingWidgetOpen: boolean;
  setIsFloatingWidgetOpen: (open?: boolean) => void;
  isFloatingWidgetHidden: boolean;
  setIsFloatingWidgetHidden: React.Dispatch<React.SetStateAction<boolean>>;
  floatingVariant: TFloatingVariants;
  setFloatingVariant: React.Dispatch<React.SetStateAction<TFloatingVariants>>;
  floatingVariantConfig: IFloatingVariantConfig | null;
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  navigateIframe: (url: string) => void;
  postMessage: (
    type: IMessagePayload['type'],
    data: IMessagePayload['data']
  ) => void;
  readyEvents: Set<IMessagePayload['type']>;
  resetReadyEvents: () => void;
}

const FloatingWidgetContext = createContext<IFloatingWidgetContext | null>(
  null
);

type TFloatingVariants = 'support-center' | 'claims-bot' | 'cult-bot' | null;

const FloatingVariantRoutes: Array<{
  route: string | RegExp;
  variant: TFloatingVariants;
}> = [
  {
    /**
     * Matches the cult promo subtree (/retail/cult-fit and its children)
     */
    route: /^\/retail\/cult-fit(\/.*)?$/,
    variant: 'cult-bot',
  },

  {
    /**
     * Matches the /claims/:claimId route, excluding specific paths including "upload"
     */
    route:
      /^\/claims\/(?!create|network-hospitals|all|redirect$)(?!.*upload)([a-zA-Z0-9]*[0-9][a-zA-Z0-9]*).*/,
    variant: 'claims-bot',
  },

  {
    /**
     * Matches all other claims routes, excluding "upload" | "all" paths
     */
    route: /^(?!.*upload)(?:\/(?:all)?)?$/,
    variant: 'support-center',
  },
];

const getVariantForRoute = (url: string) => {
  let newVariant: TFloatingVariants = null;
  FloatingVariantRoutes.forEach(({ route, variant }) => {
    window.dev.i('Floating Widget Context: ', route, variant);
    if (typeof route === 'string') {
      if (route === url) {
        newVariant = variant;
      }
    } else if (route instanceof RegExp) {
      if (route.test(url)) {
        newVariant = variant;
      }
    }
  });

  window.dev.i('Floating Widget Context: ', 'NEW VARIANT', newVariant);

  return newVariant;
};

interface IFloatingVariantConfig {
  // add regex
  excludedRoutes?: Array<string>;
  iframeUrl: URL;
  TriggerComponent: React.FC<IFloatingVariantProps>;
  visibleInWebView?: boolean;
  variant: TFloatingVariants;
}

const SUPPORT_CENTER_BASE_URL = AppConfig.SUPPORT_CENTER_BASE_URL as string;

const CHAT_FRAMEWORK_BASE_URL = AppConfig.CHAT_FRAMEWORK_BASE_URL;

const VariantConfigs = new Map<TFloatingVariants, IFloatingVariantConfig>([
  [
    'support-center',
    {
      iframeUrl: new URL(SUPPORT_CENTER_BASE_URL),
      TriggerComponent: SupportCenter,
      visibleInWebView: false,
      variant: 'support-center',
    },
  ],
  [
    'claims-bot',
    {
      iframeUrl: new URL(`${CHAT_FRAMEWORK_BASE_URL}/bot/cashless_support_bot`),
      TriggerComponent: ClaimsBot,
      visibleInWebView: true,
      variant: 'claims-bot',
    },
  ],
  [
    'cult-bot',
    {
      iframeUrl: new URL(`${CHAT_FRAMEWORK_BASE_URL}/bot/cult_support_bot`),
      TriggerComponent: CultBot,
      visibleInWebView: true,
      variant: 'cult-bot',
    },
  ],
]);

const FloatingWidgetProvider = ({ children }: PropsWithChildren<unknown>) => {
  const {
    isOpen: isFloatingWidgetOpen,
    onOpenChange: setIsFloatingWidgetOpen,
  } = useModalRoute({
    additions: { sw: 'true' },
  });
  const [isFloatingWidgetHidden, setIsFloatingWidgetHidden] =
    useState<boolean>(false);

  const [floatingVariant, setFloatingVariant] =
    useState<TFloatingVariants>('support-center');

  const [readyEvents, setReadyEvents] = useState<Set<IMessagePayload['type']>>(
    new Set()
  );

  const iframeRef = useRef(
    null
  ) as React.MutableRefObject<HTMLIFrameElement | null>;

  const { getModalParams } = useModalRoute({
    additions: { sam: 'true', sa: 'all' },
  });

  const [_, setSearchParams] = useSearchParams();

  /**
   * This useEffect listens for messages from the child iframe
   */
  useEffect(() => {
    window.dev.i(
      'Floating Widget Context: ',
      'ADDING_MESSAGE_LISTENER for child iframe'
    );
    const messageListener = (event: MessageEvent) => {
      const { data: message, origin } = event;
      if (ALLOWED_CHILD_ORIGINS.includes(origin)) {
        window.dev.i(
          'Floating Widget Context: ',
          'RECEIVED MESSAGE IN CLAIMS_APP',
          origin,
          message
        );
        const payload = safeJsonParse(message) as IMessagePayload;
        if (payload == null) {
          return;
        }
        window.dev.i(
          'Floating Widget Context: ',
          '2. AUTH_TRANSFER: RECEIVED READY EVENT',
          payload
        );
        if (payload?.type === EMessageType.READY) {
          window.dev.i(
            'Floating Widget Context: ',
            'CLAIMS_APP',
            payload?.data,
            'SETTING_READY_EVENTS'
          );
          if (Array.isArray(payload?.data)) {
            setReadyEvents((prev) => {
              const draft = new Set<IMessagePayload['type']>(prev);
              (payload?.data as Array<IMessagePayload['type']>)?.forEach(
                (event) => {
                  draft.add(event);
                }
              );
              return draft;
            });
          }
        }

        if (payload?.type === EMessageType.OPEN_ARTICLE) {
          const articleParams = getModalParams({
            sa: payload?.data?.toString?.(),
            sam: 'true',
          });

          setSearchParams(articleParams);
        }

        if (payload?.type === EMessageType.CLOSE_IFRAME) {
          setIsFloatingWidgetOpen(!payload?.data);
        }

        if (payload?.type === EMessageType.NB_CALLBACK) {
          // Privileged path: only the active bot iframe may drive the native
          // bridge, so verify the sender window, not just the origin.
          if (
            isTrustedRelaySource(event.source, iframeRef.current?.contentWindow)
          ) {
            runRelayedNativeBridgeCall(payload?.data);
          } else {
            window.dev.i(
              'Floating Widget Context: ',
              'NB_CALLBACK from an untrusted source — ignored'
            );
          }
        }
      }
    };

    window.addEventListener('message', messageListener);

    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, [setReadyEvents]);

  const resetReadyEvents = useCallback(() => {
    setReadyEvents((prev) => {
      const draft = new Set<IMessagePayload['type']>(prev);
      [...draft.entries()]?.forEach(([event]) => {
        if (event !== EMessageType.TOKEN) {
          draft.delete(event);
        }
      });
      return draft;
    });
  }, [setReadyEvents]);

  const location = useLocation();

  useEffect(() => {
    const newVariant = getVariantForRoute(location.pathname);
    setFloatingVariant((prev) => {
      if (prev != newVariant) {
        resetReadyEvents();
        return newVariant;
      }
      return newVariant;
    });
  }, [location, resetReadyEvents]);

  // The bot iframe remounts per claim (FloatingWidget keys it by claimId),
  // which drops the child's listeners. Clear stale ready events so prompt tiles
  // buffer until the new iframe re-advertises instead of posting into the void.
  const activeClaimId =
    getVariantForRoute(location.pathname) === 'claims-bot'
      ? location.pathname.split('/')[2] ?? ''
      : '';
  useEffect(() => {
    resetReadyEvents();
  }, [activeClaimId, resetReadyEvents]);

  const contextValue = useMemo(() => {
    const floatingVariantConfig = VariantConfigs.get(floatingVariant) ?? null;

    const navigateIframe = (url: string) => {
      window.dev.i(
        'Floating Widget Context: ',
        'CLAIMS_APP',
        'NAVIGATING IFRAME',
        url
      );
      if (iframeRef.current && iframeRef.current.contentWindow) {
        iframeRef.current.contentWindow.location.href = `${floatingVariantConfig?.iframeUrl?.origin}${url}`;
      }
    };

    return {
      floatingVariant,
      floatingVariantConfig,
      setFloatingVariant,
      navigateIframe,
      postMessage: (
        type: IMessagePayload['type'],
        data: IMessagePayload['data']
      ) => {
        postMessage(
          type,
          data,
          iframeRef.current,
          floatingVariantConfig?.iframeUrl?.origin ?? ''
        );
      },
      readyEvents,
    };
  }, [floatingVariant, setFloatingVariant, readyEvents]);

  return (
    <FloatingWidgetContext.Provider
      value={{
        ...contextValue,
        isFloatingWidgetOpen,
        setIsFloatingWidgetOpen,
        isFloatingWidgetHidden,
        setIsFloatingWidgetHidden,
        resetReadyEvents,
        iframeRef,
      }}
    >
      {children}
    </FloatingWidgetContext.Provider>
  );
};

const useFloatingWidgetContext = () => {
  const context = useContext(FloatingWidgetContext);
  if (!context) {
    throw new Error(
      'Cannot use context without wrapping it inside the Provider'
    );
  }
  return context;
};

export default FloatingWidgetProvider;
export { useFloatingWidgetContext, FloatingWidgetContext };
